import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { MapPin, MessageSquare } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import SectionHead from "@/components/home/SectionHead";

export default function PlayersNearYou({ players, availableNow = 0 }) {
  const navigate = useNavigate();
  const challenge = async (p) => {
    try {
      await http.post("/challenges", { opponent_id: p.user_id, match_type: "singles", message: "Let's play!" });
      toast.success(`Challenge sent to ${p.first_name}`);
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  const [busyChip, setBusyChip] = useState(null);
  const proposeTime = async (p, t) => {
    const key = p.user_id + t;
    if (busyChip) return;
    setBusyChip(key);
    try {
      await http.post("/challenges", { opponent_id: p.user_id, match_type: "singles", proposed_time: t, message: `Free to play ${t}?` });
      toast.success(`Proposed ${t} to ${p.first_name}`);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusyChip(null);
    }
  };
  return (
    <section data-testid="players-near-carousel">
      <SectionHead title="Available to Play" onSee={() => navigate("/play")} seeLabel="See All Players" seeTestid="see-all-players-btn" />
      <p className="text-sm text-[#5C7285] -mt-2 mb-3" data-testid="available-now-count">
        {availableNow > 0 ? `${availableNow} player${availableNow > 1 ? "s" : ""} free when you are tonight.` : "Players sorted by how often they're free when you are."}
      </p>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
        {players.map((p) => (
          <div key={p.user_id} data-testid={`player-card-${p.user_id}`} className="snap-start shrink-0 w-44 rounded-2xl bg-white border border-[#0E395F]/10 p-4">
            <Avatar className="h-14 w-14 mx-auto border-2 pv-border-primary">
              <AvatarImage src={p.avatar} alt={p.first_name} />
              <AvatarFallback>{p.first_name[0]}</AvatarFallback>
            </Avatar>
            <div className="text-center mt-2 font-bold pv-text-dark truncate">{p.first_name}</div>
            <div className="text-center text-lg font-black font-display pv-text-dark">
              {p.dupr.toFixed(2)} <span className="text-[10px] text-[#5C7285]">DUPR</span>
            </div>
            <div className="flex items-center justify-center gap-1 text-xs text-[#5C7285]"><MapPin size={11} />{p.distance_mi} mi</div>
            <div className="mt-1 text-center min-h-[20px]">
              {p.free_now ? (
                <span className="inline-block rounded-full bg-[#F5B700] text-black text-[10px] font-black px-2 py-0.5 animate-pulse" data-testid={`free-now-badge-${p.user_id}`}>● Free right now</span>
              ) : p.free_tonight ? (
                <span className="inline-block rounded-full bg-[#005F19]/10 text-[#005F19] text-[10px] font-bold px-2 py-0.5">Free tonight</span>
              ) : p.shared_slots > 0 ? (
                <span className="inline-block rounded-full bg-[#4A90E2]/10 text-[#4A90E2] text-[10px] font-bold px-2 py-0.5">{p.shared_slots} shared time{p.shared_slots > 1 ? "s" : ""}</span>
              ) : (
                <span className="inline-block rounded-full bg-black/5 text-[#5C7285] text-[10px] font-bold px-2 py-0.5">{p.availability}</span>
              )}
            </div>
            {p.shared_times && p.shared_times.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1 justify-center" data-testid={`shared-times-${p.user_id}`}>
                {p.shared_times.map((t, i) => (
                  <button
                    key={i}
                    data-testid={`propose-time-${p.user_id}-${i}`}
                    onClick={() => proposeTime(p, t)}
                    disabled={busyChip === p.user_id + t}
                    title={`Propose a match ${t}`}
                    className="rounded bg-[#BED445]/25 text-[#3d4a12] text-[9px] font-bold px-1.5 py-0.5 hover:bg-[#BED445] transition-colors disabled:opacity-50"
                  >
                    {t}
                  </button>
                ))}
                {p.shared_times_more > 0 && <span className="text-[9px] text-[#5C7285] font-bold self-center">+{p.shared_times_more}</span>}
              </div>
            )}
            <button
              data-testid={`challenge-btn-${p.user_id}`}
              onClick={() => challenge(p)}
              className="mt-3 w-full bg-[#F5B700] text-black font-black rounded-full py-2 text-xs hover:bg-[#ffca2c] transition-colors"
            >
              Challenge
            </button>
            <button
              data-testid={`message-btn-${p.user_id}`}
              onClick={() => navigate(`/messages?to=${p.user_id}`)}
              className="mt-2 w-full border border-[#0E395F]/15 text-[#0E395F] font-bold rounded-full py-1.5 text-xs hover:bg-[#0E395F]/5 transition-colors inline-flex items-center justify-center gap-1"
            >
              <MessageSquare size={12} /> Message
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}
